import { useState, useEffect } from "react";
import { AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { api } from "../../api/base";
import { useSnackBar } from "../../context/SnackBar";
import { setLoginModalOpen } from "../../redux/actions/app";

export const LoginPage = (): JSX.Element => {
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] =
    useState<boolean>(false);

  const openForgotPasswordModal = (): void => {
    setForgotPasswordModalOpen(true);
  };

  const closeForgotPasswordModal = (): void => {
    setForgotPasswordModalOpen(false);
  };

  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const dispatch = useDispatch();

  const [userData, setUserData] = useState<{ email: string; password: string }>(
    {
      email: "",
      password: "",
    },
  );

  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const validateForm = async (): Promise<void> => {
    if (isValidEmail(userData.email) && isValidPassword(userData.password)) {
      await loginUser();
    }
  };

  const isValidPassword = (password: string): boolean => {
    setShowPasswordError(false);
    if (!password) {
      setShowPasswordError(true);
      setPasswordError("This is a required field.");
      return false;
    }
    return true;
  };

  const isValidEmail = (email: string): boolean => {
    setShowEmailError(false);
    if (!email) {
      setShowEmailError(true);
      setEmailError("This is a required field.");
      return false;
    }
    if (!validateEmailFormat(userData.email)) {
      setShowEmailError(true);
      setEmailError("Please enter a valid email.");
      return false;
    }
    return true;
  };

  const validateEmailFormat = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const loginUser = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> = await api.post("auth/login", {
        email: userData.email,
        password: userData.password,
        platform: "web",
        sessionId: localStorage.getItem("sessionId"),
        deviceId: localStorage.getItem("device_id"),
      });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      showSnackBar(
        "Successfully logged in!",
        "success",
        "center",
        2000,
        false,
        "auto",
        "95px",
      );
      
      // Check if there's a plan parameter to redirect appropriately
      const urlParams = new URLSearchParams(window.location.search);
      const planParam = urlParams.get("plan");
      
      if (planParam === "hardware") {
        navigate("/hardware?purchase=hardware");
      } else if (planParam === "density-pro") {
        navigate("/pricing?purchase=density-pro");
      } else {
        navigate("/account");
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response) {
        if (err.response.status === 401) {
          console.log(err.response);
          if (err.response.data === "Password is incorrect") {
            setShowPasswordError(true);
            setPasswordError("Password is incorrect.");
          } else {
            setShowEmailError(true);
            setEmailError("Email is incorrect.");
          }
        } else if (err.response.status === 500) {
          setShowEmailError(true);
          setEmailError("Server error. Please try again later.");
        } else {
          setShowEmailError(true);
          setEmailError(err?.response?.data?.msg || err?.response?.data || "Login failed");
        }
      } else if (err.request) {
        // Request was made but no response received
        setShowEmailError(true);
        setEmailError("Unable to connect to server. Please check your connection.");
      } else {
        // Something else happened
        setShowEmailError(true);
        setEmailError("An unexpected error occurred. Please try again.");
      }
    }
  };

  useEffect(() => {
    navigate(`/${window.location.search}`);
    dispatch(setLoginModalOpen(true));
    // analyticsEvent("PAGE_VISIT_V1", {
    //   page: "Landing",
    //   section: "Login",
    // })
    // );
  }, []);

  return <></>;
};
